import { environment } from '@env/environment';
import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { AuthService } from '@app/auth.service';


export interface GetListResponse {
    count: number;
    limit: number;
    offset: number;
}

export interface ModelBase {
    id: number;
    created_date?: string;
    created_user_id?: number;
    deleted_date?: string;
    deleted_user_id?: number;
    is_deleted: boolean;
    modified_date?: string;
    modified_user_id?: number;
}

export class ApiError {
    data?: any;
    statusCode: number;
    text?: string;

    constructor(args: {
        data?: any
        statusCode: number,
        text?: string,
    }) {
        const {statusCode, text, data} = args;
        this.statusCode = statusCode;
        this.text = text;
        this.data = data;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ApiService {

    public baseUrl: string = environment.api_base_url;
    private refreshTokenPromise: Promise<any> = null;

    constructor(private authService: AuthService) {
        //
    }

    public async getIndex(): Promise<any> {
        return this.getResponseJson({urlPart: '/'});
    }

    public async getResponseJson(args: {
        urlPart: string,
        queryStringParams?: Record<string, any>,
        method?: 'GET' | 'POST' | 'DELETE' | 'PUT',
        data?: Record<string, any>
    }): Promise<any> {
        const {urlPart, queryStringParams, method, data} = args;

        // Form the URL
        let url = `${this.baseUrl}${urlPart}`;

        // Set Authorization header
        const authorizationHeader = await this.authService.getAuthorizationHeader();
        const headers = {
            'Authorization': authorizationHeader
        };

        // If posting JSON, set the request body and Content-Type header
        let body = null;
        if (method === 'POST' || 'PUT' && data != null) {
            body = JSON.stringify(data);
            headers['Content-Type'] = 'application/json';
        }

        // Add query string parameters to the URL
        if (queryStringParams) {
            const usp = new URLSearchParams();
            for (const [key, value] of Object.entries(queryStringParams)) {
                if (value !== '' && value != null) {
                    usp.append(key, value);
                }
            }
            const queryString = usp.toString();
            url += `?${queryString}`;
        }

        return this.fetchResponseWithTokenRefresh(url, method, headers, body);
    }

    private async fetchResponseWithTokenRefresh(url, method, headers, body) {
        try {
            return await this.fetchResponse(url, method, headers, body);
        }
        catch (error) {
            if (error.statusCode === 401) {
                // Refresh the authorization token and try again
                try {
                    const authorizationHeader = await this.refreshAuthTokenOrWait();
                    headers['Authorization'] = authorizationHeader;
                    return this.fetchResponse(url, method, headers, body);
                }
                catch (innerError) {
                    throw innerError;
                }
            }
            else {
                throw error;
            }
        }
    }

    /**
     * Refresh the auth token, or if an auth token refresh is already
     * in progress, wait for the refresh to complete. This ensures that
     * requests made in quick succession don't trigger multiple token
     * refreshes.
     *
     * Returns the new Authorization header string.
     */
    private async refreshAuthTokenOrWait() {
        if (!this.refreshTokenPromise) {
            // Create promise and perform refresh
            this.refreshTokenPromise = new Promise(async (resolve, reject) => {
                try {
                    await this.authService.refreshAuthToken();
                    const authHeader = await this.authService.getAuthorizationHeader();
                    this.refreshTokenPromise = null;
                    resolve(authHeader);
                }
                catch (ex) {
                    reject(ex);
                }
            });
        }
        return await this.refreshTokenPromise;
    }

    private async fetchResponse(url, method, headers, body) {
        const response = await fetch(url, {
            method: method || 'GET',
            headers: headers,
            body: body,
            credentials: 'include'
        });
        // If the response is not OK, throw an ApiError, attempting
        // to parse the response to JSON. If that fails, the response
        // text is included in the error instead.
        if (!response.ok) {
            let respData = null;
            let respText = null;

            try {
                respData = await response.json();
            }
            catch (ex) {
                respText = await response.text();
            }

            throw new ApiError({
                data: respData,
                statusCode: response.status,
                text: respText
            });
        }

        const contentType = response.headers.get('content-type');
        // If we fetch an image, handle it appropriately
        if (contentType && contentType.indexOf('image/png') !== -1) {
            return response.blob();
        }
        // Return response JSON
        return response.json();
    }
}
